import { useCallback, useRef, useState } from "react";
import { getCsrfToken } from "@/lib/csrf";

export interface Message {
	id: string;
	role: "user" | "bot";
	content: string;
	therapists?: TherapistResult[];
	extractedParams?: Record<string, string | null>;
	searchContext?: SearchContext | null;
}

export interface SearchContext {
	lat: number;
	lng: number;
	city: string;
	datetime: string;
	gender: string | null;
	specialization: string | null;
	serviceId: number | null;
}

export interface TherapistResult {
	id: string;
	name: string;
	gender: string;
	employmentType: string;
	employmentStatus: string;
	registrationNumber: string;
	specializations: string[];
	modalities: string[];
	distanceKm: number | null;
	serviceName: string | null;
	activeAddress: {
		address: string;
		latitude: number;
		longitude: number;
		location: {
			id: number;
			city: string;
			state: string;
			country: string;
		} | null;
	} | null;
}

interface ChatResponse {
	reply: string;
	therapists: TherapistResult[];
	extractedParams: Record<string, string | null>;
	searchContext: SearchContext | null;
}

export function useChat(searchUrl: string) {
	const [messages, setMessages] = useState<Message[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const abortControllerRef = useRef<AbortController | null>(null);

	const sendMessage = useCallback(
		async (text: string) => {
			const userMsg: Message = {
				id: crypto.randomUUID(),
				role: "user",
				content: text,
			};

			setMessages((prev) => [...prev, userMsg]);
			setIsLoading(true);

			const conversationHistory = messages.map((m) => ({
				role: m.role === "bot" ? "assistant" : "user",
				content: m.content,
			}));

			abortControllerRef.current?.abort();
			const controller = new AbortController();
			abortControllerRef.current = controller;

			try {
				const res = await fetch(searchUrl, {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						"X-CSRF-Token": getCsrfToken(),
					},
					body: JSON.stringify({
						message: text,
						conversationHistory,
					}),
					signal: controller.signal,
				});

				if (!res.ok) throw new Error(`HTTP ${res.status}`);

				const data: ChatResponse = await res.json();

				const botMsg: Message = {
					id: crypto.randomUUID(),
					role: "bot",
					content: data.reply,
					therapists: data.therapists,
					extractedParams: data.extractedParams,
					searchContext: data.searchContext,
				};

				setMessages((prev) => [...prev, botMsg]);
			} catch (err: unknown) {
				if (err instanceof DOMException && err.name === "AbortError") return;

				const errorMsg: Message = {
					id: crypto.randomUUID(),
					role: "bot",
					content: "Sorry, something went wrong. Please try again.",
				};
				setMessages((prev) => [...prev, errorMsg]);
			} finally {
				setIsLoading(false);
			}
		},
		[messages, searchUrl],
	);

	const clearMessages = useCallback(() => {
		abortControllerRef.current?.abort();
		setMessages([]);
	}, []);

	return { messages, isLoading, sendMessage, clearMessages };
}
